import {
  Pressable,
  View,
  ViewProps,
  Modal,
  ScrollView,
  StyleProp,
  TextStyle,
} from "react-native";
import React, { useMemo, useState } from "react";
import { AgreementDetails, TenantSubdomain } from "features/types";
import { LIGHT_BLUE } from "constants/";
import * as Browser from "expo-web-browser";
import env from "config/env";
import { useAppSelector } from "hooks/useAppSelector";
import { Divider, Text, TextProps } from "@ui-kitten/components";
import {
  AgreementVisibility,
  useFetchAgreementsQuery,
} from "queries/agreement.queries";
import BaseCard, { BaseCardListItem } from "./BaseCard";
import Chevron from "./Chevron";
import useIsRunningInBallpark from "hooks/useIsRunningInBallpark";

interface AgreementTextProps extends ViewProps {
  btnText?: string;
  visibility?: AgreementVisibility;
  variant?: "TEXT" | "CARD" | "FOOTER";
  textProps?: TextProps;
  textStyle?: TextStyle;
}

const MLB_PRIVACY_POLICY = "MLB Privacy Policy";
const MLB_TERMS_OF_SERVICE = "MLB Terms of Use";

export const getUrl = (tenantSubdomain: TenantSubdomain | null) => {
  if (!tenantSubdomain) return "";
  return `${env.rootUrl.replace("tenantSubdomain", tenantSubdomain)}`;
};

const getEncodedUri = (uri: string) => encodeURI(uri);

const AgreementText: React.FC<AgreementTextProps> = ({
  btnText,
  visibility = "REGISTRATION",
  variant = "TEXT",
  textProps,
  textStyle,
  ...rest
}) => {
  const { data: agreements } = useFetchAgreementsQuery(visibility);
  const tenantSubdomain = useAppSelector(
    (state) => state.tenant.tenantSubdomain
  );
  const { isRunningInBallpark } = useIsRunningInBallpark();
  const [modalVisible, setModalVisible] = useState(false);

  const filteredAgreements = useMemo(() => {
    if (!agreements) return [];
    // If app is running in ballpark, only show MLB agreements
    if (isRunningInBallpark) {
      return agreements.filter(
        (agreement) =>
          agreement.title === MLB_PRIVACY_POLICY ||
          agreement.title === MLB_TERMS_OF_SERVICE
      );
    }
    return agreements;
  }, [agreements, isRunningInBallpark]);

  const getTextUrl = (title: string) =>
    getEncodedUri(getUrl(tenantSubdomain) + `/legal/${title}`);
  const getFileUrl = (fileIdentifier: string) =>
    getEncodedUri(getUrl(tenantSubdomain) + `/agreements/${fileIdentifier}`);

  const openLinkInExternalBrowser = async (agreement: AgreementDetails) => {
    const { fileIdentifier, url, type } = agreement;
    switch (type) {
      case "TEXT": {
        await Browser.openBrowserAsync(getTextUrl(agreement.title));
        break;
      }
      case "FILE": {
        await Browser.openBrowserAsync(getFileUrl(fileIdentifier));
        break;
      }
      case "URL": {
        await Browser.openBrowserAsync(url);
        break;
      }
      default:
        break;
    }
  };
  const handleOpenLink = async (agreement: AgreementDetails) => {
    openLinkInExternalBrowser(agreement);
  };

  if (!filteredAgreements) return null;

  return (
    <View>
      {variant === "TEXT" && btnText !== undefined && (
        <View {...rest}>
          <Text {...textProps}>
            By selecting "{btnText}", you acknowledge that you have read and
            agree to our
            <>
              {filteredAgreements.map((agreement, i) => (
                <Text key={i}>
                  <Pressable
                    key={agreement.id}
                    onPress={() => handleOpenLink(agreement)}
                    style={{ margin: 0, padding: 0 }}
                  >
                    <Text style={{ color: LIGHT_BLUE }} {...textProps}>
                      {i === 0 ? " " : ""}
                      {agreement.title}
                    </Text>
                  </Pressable>
                  <Text {...textProps}>
                    {i < filteredAgreements.length - 2 ? ", " : ""}
                    {i === filteredAgreements.length - 2 ? " and " : ""}
                  </Text>
                </Text>
              ))}
            </>
            .
          </Text>
        </View>
      )}
      {variant === "CARD" && (
        <BaseCard title="Privacy">
          {filteredAgreements.map((agreement) => (
            <BaseCardListItem
              key={agreement.id}
              title={agreement.title}
              onPress={() => handleOpenLink(agreement)}
            ></BaseCardListItem>
          ))}
        </BaseCard>
      )}
      {variant === "FOOTER" && (
        <View className="flex-row gap-2 justify-center">
          {filteredAgreements.map((agreement) => (
            <View key={agreement.id}>
              <Pressable onPress={() => handleOpenLink(agreement)}>
                <Text style={textStyle}>{agreement.title}</Text>
              </Pressable>
            </View>
          ))}
        </View>
      )}
      <Modal visible={modalVisible} animationType="slide">
        <ScrollView contentContainerStyle={{ flex: 1 }}>
          <Pressable
            onPress={() => setModalVisible(false)}
            style={{
              flexDirection: "row",
              alignItems: "center",
              paddingLeft: 5,
              paddingTop: 5,
            }}
          >
            <Chevron direction="left" color={LIGHT_BLUE}></Chevron>
            <Text status="info">Back</Text>
          </Pressable>
          <Divider />
        </ScrollView>
      </Modal>
    </View>
  );
};

export default AgreementText;
